<template>
	<div class='dashboard-app'>
        <div class='app-content'>
        <div class='headline-container'>
            <h1>SDK Keys</h1>
            <div class="search-container">
                <div class="input-container">
                    <v-icon :icon="'fas fa-magnifying-glass'"></v-icon>
                    <input id="search-bar" v-model="searchTerm" @input="handleSearch" placeholder="Enter a key name or bundle ID">
                </div>
            </div>

            <span id='create-button' :style="{ visibility: canCreateSdkKey ? 'visible' : 'hidden' }">
            <v-btn id="create-button-btn" class="str-btn-primary" @click='showDynamicModalFunction(true)'>+ Add SDK Key</v-btn>
            </span>
        </div>

        <!-- SDK Keys Table -->
        <div class='table-container'>
            <KeysTable @key-copied="showSnackbar" />
        </div>
        <div class="pagination-container" v-if="totalPages > 1">
            <div
                class="pagination-page"
                v-for="page in pageNumbers"
                @click="goToPage(page.pageNumber)"
                :key="page.pageNumber"
                :class="{ 'pagination-page-active': page.isActive }"
                >
                {{ page.pageNumber }}
            </div>
        </div>
        <DynamicFormModal
            v-if='showDynamicModal'
            :show-modal='showDynamicModal'
            :emit-function='emitFormEvent'
            @update:show-modal='showDynamicModalFunction'
        />
        <DeleteConfirmationModal
            v-if='showDeleteConfirmationModal'
            :itemData='deleteItemData'
            :show-modal='showDeleteConfirmationModal'
            @confirm-delete='handleDeleteConfirmation'
            @cancel-delete='closeDeleteConfirmationModal'
        />
        <ErrorModal
            v-if='showErrorModal'
            :show-modal='showErrorModal'
            :error-message='apiError'
            @on-close='closeErrorModal'
        />
        <LoadingModal
            v-if="showLoadingModal"
            :show-modal="showLoadingModal"
        />
        <KeyModal
            v-if='showKeyModal'
            :show-modal='showKeyModal'
            :key-value='keyValue'
            @update:show-modal='closeKeyModal'
        />
        </div>
         <v-snackbar
            v-model="snackbar"
            :timeout="2000"
            >
            {{ snackbarText }}
            <template v-slot:actions>
                <v-btn
                color="blue"
                variant="text"
                @click="snackbar = false"
                >
                Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>

</template>


<script>
import { computed, watch, handleError, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useCBERequest } from '../composables.js';
import { useAuth0 } from '@auth0/auth0-vue';

import KeysTable from '@/components/KeysTable.vue';

import DynamicFormModal from '../modals/DynamicFormModal.vue';
import DeleteConfirmationModal from '../modals/DeleteConfirmationModal.vue';
import ErrorModal from '../modals/ErrorModal.vue';
import LoadingModal from '../modals/LoadingModal.vue';
import KeyModal from '../modals/KeyModal.vue';

export default {
    name: 'KeysVault',
    components: {
        KeysTable,
        DynamicFormModal,
        DeleteConfirmationModal,
        ErrorModal,
        LoadingModal,
        KeyModal,
    },
    setup() {
        const store = useStore();
        const { makeCBERequest } = useCBERequest();
        const { getAccessTokenSilently, user } = useAuth0();

        const apiError = computed(() => store.state.errorMessage);
        const keyValue = computed(() => store.state.keyValue);
        const deleteItemData = computed(() => store.state.deleteItemData);
        const apiResponseObject = computed(() => store.state.apiResponseObject || []);
        const totalRows = computed(() => store.state.totalRows);
        const searchTerm = ref('');
        const currentPage = computed(() => store.state.currentPage);
        const allSdkResponses = ref([]);
        const fullResponseObject = computed(() => store.state.fullResponseObject || []);
        const snackbar = ref(false);
        const snackbarText = ref('');

        const showDynamicModal = computed(() => store.state.showDynamicModal);
        const showDeleteConfirmationModal = computed(() => store.state.showDeleteConfirmationModal);
        const showErrorModal = computed(() => store.state.showErrorModal);
        const showLoadingModal = computed(() => store.state.showLoadingModal);
        const showKeyModal = computed(() => store.state.showKeyModal);

        const sdkKeyCount = computed(() => allSdkResponses.value.length);

        const canCreateSdkKey = computed(() => {
            if (!user.value) {
                return false;
            }

            const userRoles = user.value['https://structure.io/roles'] || [];
            const limitedRoles = ['sdk-access-basic', 'sdk-access-professional', 'sdk-access-premium'];

            const hasLimitedRole = userRoles.some(role => limitedRoles.includes(role));

            if (hasLimitedRole) {
                return sdkKeyCount.value < 1;
            }

            // For other roles (e.g., Enterprise), allow creation.
            return true;
        });


        onMounted(async () => {
            await fetchTableData();
        });

        const showSnackbar = (text) => {
            snackbarText.value = text;
            snackbar.value = true;
        };

        const emitFormEvent = async (eventType, formData) => {
            switch (eventType) {
                case 'submit-sdk-keys-form':
                    try {
                        await handleSdkKeysFormSubmit(formData);
                    } catch(error) {
                        handleError(error);
                        await fetchTableData();
                    }
                    break;
            }
        };

        //function that submits data to create new SDK Key
        const handleSdkKeysFormSubmit = async (formData) => {
            if (!canCreateSdkKey.value) {
                store.commit('SET_ERROR_MESSAGE', 'You have reached the maximum number of SDK keys for your subscription level.');
                store.commit('SET_SHOW_ERROR_MODAL', true);
                store.commit('SET_SHOW_DYNAMIC_MODAL', false);
                return;
            }
            const endpoint = '/piccolo/sdk-keys';
            const data = {
                name: formData.name,
                bundle_id: formData.bundle,
                improved_billing: formData.improvedBilling,
            };
            const params = {};

            const request = await makeCBERequest(endpoint, 'post', data, params);
            store.commit('SET_KEY_VALUE', request);
            await fetchTableData();

            store.commit('SET_SHOW_DYNAMIC_MODAL', false);
            store.commit('SET_SHOW_KEY_MODAL', true);
        }

        //function that populates the table
        const fetchTableData = async () => {
            const endpoint = '/piccolo/sdk-keys';
            const data = {};
            const params = { limit: '50' };
            let fullResponse = [];

            try {
                store.commit('SET_SHOW_LOADING_MODAL', true);
                let offset = 0;
                let apiResponses;

                do {
                    params.offset = offset.toString();
                    apiResponses = await makeCBERequest(endpoint, 'get', data, params);

                    if (!apiResponses || apiResponses.length === 0) {
                        break;
                    }
                    fullResponse.push(...apiResponses);
                    offset += parseInt(params.limit);
                } while (apiResponses && apiResponses.length > 0);

                allSdkResponses.value = fullResponse;

                if (searchTerm.value) {
                    handleSearch();
                } else {
                    store.commit('SET_FULL_RESPONSE_OBJECT', fullResponse);
                    store.commit('SET_TOTAL_ROWS', fullResponse.length);
                    store.commit('SET_API_RESPONSE_OBJECT', fullResponse.slice(0, 25));
                    store.commit('SET_CURRENT_PAGE', 1);
                }
                store.commit('SET_SHOW_LOADING_MODAL', false);
            } catch (error) {
                handleApiError(error);
            }
        };

        //function that filters based on search input
        const handleSearch = () => {
            const activeResponses = allSdkResponses.value;
            let filteredData;

            if (searchTerm.value) {
                const search = searchTerm.value.toLowerCase();
                filteredData = activeResponses.filter(item =>
                    item?.name?.toLowerCase()?.includes(search) ||
                    item?.bundle_id?.toLowerCase()?.includes(search)
                );
            } else {
                filteredData = activeResponses;
            }

            store.commit('SET_FULL_RESPONSE_OBJECT', filteredData);
            store.commit('SET_API_RESPONSE_OBJECT', filteredData.slice(0, 25));
            store.commit('SET_TOTAL_ROWS', filteredData.length || 0);
            store.commit('SET_CURRENT_PAGE', 1);
        };

        const totalPages = computed(() => Math.ceil(totalRows.value / 25));

        // Array of page numbers
        const pageNumbers = computed(() => {
            const pages = Array.from({ length: totalPages.value }, (_, index) => {
                let pageNumber = index + 1;
                const isActive = pageNumber === currentPage.value;
                return { pageNumber, isActive };
            });
            return pages;
        });

        // Function to update the current page and fetch data
        const goToPage = (pageNumber) => {
            store.commit('SET_CURRENT_PAGE', pageNumber);
            const activeResponses = fullResponseObject.value;

            const startIndex = (pageNumber - 1) * 25;
            const endIndex = pageNumber * 25;
            const slicedData = activeResponses.slice(startIndex, endIndex);
            store.commit('SET_API_RESPONSE_OBJECT', slicedData);

            store.commit('SET_TOTAL_PAGES', Math.ceil(store.state.totalRows / 25));

            if (pageNumber === totalPages.value && activeResponses.length % 25 !== 0) {
                const remainingItems = activeResponses.length % 25;
                store.commit('SET_API_RESPONSE_OBJECT', activeResponses.slice(startIndex, startIndex + remainingItems));
            }
        };

        //function that controls whether the dynamic modal is shown or not
        const showDynamicModalFunction = (value) => {
            store.commit('SET_SHOW_DYNAMIC_MODAL', value);
        }

        // Function to handle API request errors
        const handleApiError = async (error) => {
            store.commit('SET_SHOW_LOADING_MODAL', false);
            if (error.response && error.response.status === 401) {
                const authData = {
                    accessToken: null,
                    username: '',
                    password: '',
                    superuserStatus: false,
                };
                store.commit('SET_AUTHENTICATION', authData);
                const loginData = {
                    loginEmail: '',
                    loginPassword: '',
                }
                store.commit('SET_LOGIN_FORM_DATA', loginData);
                store.commit('SET_IS_ACCESS_TOKEN_SET', false);
                store.commit('SET_ACCESS_TOKEN', null);
            } else if (error.response && error.response.status === 403) {
                store.commit('SET_ERROR_MESSAGE', "Permissions Error: Your permissions are not configured correctly. Please reach out to partners@structure.io for assistance.");
                store.commit('SET_SHOW_ERROR_MODAL', true);
            } else {
                store.commit('SET_ERROR_MESSAGE', error.message || 'An error occurred.');
                store.commit('SET_SHOW_ERROR_MODAL', true);
            }
        }

        //function to close error modal
        const closeErrorModal = () => {
            store.commit('SET_SHOW_ERROR_MODAL', false);
            store.commit('SET_ERROR_MESSAGE', null);
        }

        //function to close key modal
        const closeKeyModal = () => {
            store.commit('SET_SHOW_KEY_MODAL', false);
            store.commit('SET_KEY_VALUE', null);
        }

        //function that deletes the SDK key
        const handleDeleteConfirmation = async () => {
            const tokenId = store.state.deleteItemData;
            try {
                await deleteLineItem(tokenId);
            } catch {
                return;
            }

            allSdkResponses.value = allSdkResponses.value.filter(item => item.id !== tokenId);

            const updatedFullResponse = fullResponseObject.value.filter(item => item.id !== tokenId);
            store.commit('SET_FULL_RESPONSE_OBJECT', updatedFullResponse);
            store.commit('SET_TOTAL_ROWS', updatedFullResponse.length);

            let pageToGo = currentPage.value;
            const newTotalPages = Math.ceil(updatedFullResponse.length / 25);
            if (pageToGo > newTotalPages) {
                pageToGo = newTotalPages || 1;
            }
            goToPage(pageToGo);

            closeDeleteConfirmationModal();
        }

        //function that specifically deletes an item
        const deleteLineItem = async (tokenId) => {
            const endpoint = `/piccolo/sdk-keys/${tokenId}`;
            const data = {};
            const params = {};

            try {
                await makeCBERequest(endpoint, 'delete', data, params);
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        }

        //function called to request the delete modal to close
        const cancelDeleteConfirmation = () => {
            closeDeleteConfirmationModal();
        }

        //function that actually closes the delete modal and resets itemsToDelete to none
        const closeDeleteConfirmationModal = () => {
            store.commit('SET_SHOW_DELETE_CONFIRMATION_MODAL', false);
            store.commit('SET_DELETE_ITEM_DATA', null);
        }

        watch(() => searchTerm.value, () => {
            handleSearch();
        });

        return {
            apiError,
            getAccessTokenSilently,
            keyValue,
            apiResponseObject,
            showDynamicModal,
            showDynamicModalFunction,
            showDeleteConfirmationModal,
            showErrorModal,
            showLoadingModal,
            showKeyModal,
            deleteItemData,
            emitFormEvent,
            handleSdkKeysFormSubmit,
            handleDeleteConfirmation,
            cancelDeleteConfirmation,
            deleteLineItem,
            closeDeleteConfirmationModal,
            closeErrorModal,
            closeKeyModal,
            fetchTableData,
            totalRows,
            totalPages,
            pageNumbers,
            goToPage,
            handleSearch,
            searchTerm,
            fullResponseObject,
            store,
            snackbar,
            snackbarText,
            showSnackbar,
            user,
            canCreateSdkKey,
        };
    }
};
  </script>


<style scoped lang="scss">
    @import '@/assets/styles/StrVariables.scss'
    @import '@/assets/styles/DashboardApp.scss'
    @import '@/assets/styles/StrButtons.scss'
</style>